
import React, { useRef, useState, useEffect } from 'react';
import { Ambassador, ApplicationStatus } from '../types';

interface DetailModalProps {
  ambassador: Ambassador | null;
  onClose: () => void;
  onApprove: (id: string) => void;
  onOnboard: (id: string) => void;
  onArchive: (id: string) => void;
  onUpdatePhoto: (id: string, photoUrl: string) => void;
  onReverseApproval: (id: string) => void;
  onUpdateAmbassador: (updated: Ambassador) => void;
}

export const DetailModal: React.FC<DetailModalProps> = ({ 
  ambassador, 
  onClose, 
  onApprove, 
  onOnboard,
  onArchive, 
  onUpdatePhoto,
  onReverseApproval,
  onUpdateAmbassador
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Ambassador | null>(null);

  useEffect(() => {
    if (ambassador) {
      setEditData({ ...ambassador });
      setIsEditing(false);
    }
  }, [ambassador]);

  if (!ambassador || !editData) return null;

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdatePhoto(ambassador.id, reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApproveClick = async () => {
    setIsSyncing(true);
    await onApprove(ambassador.id);
    setIsSyncing(false);
  };

  const handleOnboardClick = async () => {
    setIsSyncing(true);
    await onOnboard(ambassador.id);
    setIsSyncing(false);
  };

  const handleSaveEdit = () => {
    onUpdateAmbassador(editData);
    setIsEditing(false);
  };

  const hasCredentials = ambassador.ambassadorCode && ambassador.password;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-end bg-black/40 backdrop-blur-sm p-4 md:p-10"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl bg-white rounded-[3rem] h-full shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-right duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-10 pb-6 flex justify-between items-start">
          <div className="flex gap-6 items-center flex-1">
            <div 
              onClick={handlePhotoClick}
              className="w-20 h-20 bg-purple-100 rounded-[2rem] flex items-center justify-center text-purple-600 font-bold text-3xl overflow-hidden cursor-pointer hover:ring-4 hover:ring-purple-200 transition-all relative group shrink-0"
            >
               {ambassador.profilePhotoUrl ? (
                 <img src={ambassador.profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
               ) : (
                 <span className="group-hover:opacity-0 transition-opacity">{ambassador.firstName[0]}{ambassador.lastName[0]}</span>
               )}
               <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
               </div>
               <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex gap-2">
                  <input className="text-xl font-bold text-purple-900 bg-purple-50 border-b border-purple-200 w-full p-1 outline-none" value={editData.firstName} onChange={(e) => setEditData({ ...editData, firstName: e.target.value })} />
                  <input className="text-xl font-bold text-purple-900 bg-purple-50 border-b border-purple-200 w-full p-1 outline-none" value={editData.lastName} onChange={(e) => setEditData({ ...editData, lastName: e.target.value })} />
                </div>
              ) : (
                <h2 className="text-2xl font-bold text-purple-900 tracking-tight truncate">{ambassador.firstName} {ambassador.lastName}</h2>
              )}
              <div className="flex items-center gap-2 mt-1">
                 {isEditing ? (
                    <input className="text-sm text-gray-500 font-medium bg-purple-50 border-b border-purple-200 w-full outline-none" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                 ) : (
                    <p className="text-sm text-gray-500 font-medium truncate">{ambassador.email}</p>
                 )}
                 <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter shrink-0 ${
                   ambassador.status === ApplicationStatus.SIGNED_ONBOARDED ? 'bg-green-100 text-green-700' : 
                   ambassador.status === ApplicationStatus.WELCOME_SENT ? 'bg-blue-100 text-blue-700' :
                   'bg-purple-100 text-purple-700'
                 }`}>
                   {ambassador.status}
                 </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="p-3 bg-purple-50 rounded-2xl text-purple-600 hover:bg-purple-100 transition-all" title="Edit Details">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
            )}
            <button onClick={onClose} className="p-3 bg-gray-50 rounded-2xl text-gray-400 hover:text-gray-900 transition-all">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-10 py-6 scrollbar-hide">
          {hasCredentials && (
            <div className="mb-8 p-6 bg-purple-600 rounded-3xl text-white shadow-xl shadow-purple-100 grid grid-cols-2 gap-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <svg className="w-20 h-20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 12c-2.67 0-5-1.33-5-4 0-1.6 1.05-2.92 2.5-3.56.76.43 1.6.56 2.5.56s1.74-.13 2.5-.56c1.45.64 2.5 1.96 2.5 3.56 0 2.67-2.33 4-5 4z"/></svg>
              </div>
              <div className="z-10">
                <p className="text-[8px] font-black uppercase tracking-widest text-purple-200 mb-1">Ambassador Code</p>
                <p className="text-xl font-black font-mono">{ambassador.ambassadorCode}</p>
              </div>
              <div className="z-10 text-right">
                <p className="text-[8px] font-black uppercase tracking-widest text-purple-200 mb-1">Portal Password</p>
                <p className="text-xl font-black font-mono">{ambassador.password}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100 flex flex-col justify-center">
              <p className="text-[8px] font-black uppercase text-gray-400 mb-1">Phone Number</p>
              {isEditing ? (
                <input className="text-sm font-bold text-gray-700 bg-white border border-gray-100 rounded px-2 outline-none" value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
              ) : (
                <p className="text-sm font-bold text-gray-700">{ambassador.phone}</p>
              )}
            </div>
            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100 flex flex-col gap-2">
              <div>
                <p className="text-[8px] font-black uppercase text-gray-400 mb-1">Instagram</p>
                {isEditing ? (
                  <input className="text-xs font-bold text-purple-600 bg-white border border-gray-100 rounded px-2 py-1 outline-none w-full" value={editData.instagramHandle || ''} onChange={(e) => setEditData({ ...editData, instagramHandle: e.target.value })} placeholder="@handle" />
                ) : (
                  <p className="text-xs font-bold text-purple-600">{ambassador.instagramHandle || 'None provided'}</p>
                )}
              </div>
              <div>
                <p className="text-[8px] font-black uppercase text-gray-400 mb-1">TikTok</p>
                {isEditing ? (
                  <input className="text-xs font-bold text-gray-900 bg-white border border-gray-100 rounded px-2 py-1 outline-none w-full" value={editData.tiktokHandle || ''} onChange={(e) => setEditData({ ...editData, tiktokHandle: e.target.value })} placeholder="@handle" />
                ) : (
                  <p className="text-xs font-bold text-gray-900">{ambassador.tiktokHandle || 'None provided'}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <section>
              <h4 className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-3">Application Data</h4>
              <div className="bg-purple-50/50 p-6 rounded-3xl border border-purple-100 space-y-6">
                <div>
                   <p className="text-[8px] font-black uppercase text-purple-400 mb-1">Background / Why</p>
                   {isEditing ? (
                     <textarea className="text-xs text-gray-700 font-medium leading-relaxed w-full bg-white border border-purple-100 rounded-xl p-3 outline-none" rows={3} value={editData.reason} onChange={(e) => setEditData({ ...editData, reason: e.target.value })} />
                   ) : (
                     <p className="text-sm text-gray-700 font-medium leading-relaxed">{ambassador.reason}</p>
                   )}
                </div>
                {ambassador.additionalFields.map((field, i) => (
                  <div key={i}>
                    <p className="text-[8px] font-black uppercase text-purple-400 mb-1">{field.label}</p>
                    <p className="text-sm text-gray-700 font-medium leading-relaxed">{field.value}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div className="p-10 pt-6 border-t border-purple-50 flex gap-4 bg-gray-50/30">
           {isEditing ? (
             <>
               <button onClick={handleSaveEdit} className="flex-1 bg-purple-600 text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-purple-100 hover:bg-purple-500 transition-all">Save Changes</button>
               <button onClick={() => setIsEditing(false)} className="flex-1 bg-white border border-gray-200 text-gray-500 rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-gray-100 transition-all">Cancel</button>
             </>
           ) : ambassador.status === ApplicationStatus.NEW ? (
             <>
               <button onClick={handleApproveClick} disabled={isSyncing} className="flex-1 bg-purple-600 text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-purple-100 hover:bg-purple-500 transition-all flex items-center justify-center gap-2">
                 {isSyncing ? 'Syncing...' : 'Approve & Send Welcome'}
               </button>
               <button onClick={() => onArchive(ambassador.id)} className="px-10 bg-black text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all">Archive</button>
             </>
           ) : ambassador.status === ApplicationStatus.WELCOME_SENT ? (
             <>
               <button onClick={handleOnboardClick} disabled={isSyncing} className="flex-1 bg-green-600 text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-100 hover:bg-green-500 transition-all flex items-center justify-center gap-2">
                 {isSyncing ? 'Syncing Integrations...' : 'Signed/Onboarded'}
               </button>
               <button onClick={() => onReverseApproval(ambassador.id)} className="px-10 bg-white border border-purple-200 text-purple-600 rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-all">Undo</button>
             </>
           ) : ambassador.status === ApplicationStatus.SIGNED_ONBOARDED ? (
             <>
               <button onClick={() => onArchive(ambassador.id)} className="flex-1 bg-black text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all">Move to Archive</button>
               <button onClick={() => onReverseApproval(ambassador.id)} className="px-10 bg-white border border-purple-200 text-purple-600 rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-all">Undo</button>
             </>
           ) : (
             <button onClick={() => onReverseApproval(ambassador.id)} className="w-full bg-black text-white rounded-full py-4 text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all">
               {ambassador.status === ApplicationStatus.ARCHIVED ? 'Re-Activate' : 'Move to Archive'}
             </button>
           )}
        </div>
      </div>
    </div>
  );
};
